import { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../services/api.js";
import {
  getKnowledge,
  addKnowledgeEntry,
  updateKnowledgeEntry,
  deleteKnowledgeEntry,
  getKnowledgeGroups,
  createKnowledgeGroup,
  deleteKnowledgeGroup,
  assignEntriesToGroup,
  unassignEntriesFromGroup,
  regenerateGroupSummary,
  importFromUrl,
  batchImportFromUrls,
  bulkSaveEntries,
} from "../services/knowledge.js";
import { Ava } from "../atoms/Ava.jsx";
import { ContextBudgetBar } from "../components/ContextBudgetBar.jsx";

const EMPTY_ENTRY = { topic: "", content: "", source_title: "", source_url: "", group_id: "" };

/**
 * CLONE-2 + KB-008: Admin knowledge base editor with group management.
 * Tabs: Entries | Groups | Add Entry | From URL | Stats | (Edit)
 */
export function KnowledgeAdmin({ persona, onBack, hideBackButton = false }) {
  const [tab, setTab] = useState("Entries");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [saveOk, setSaveOk] = useState(false);
  const [budgetRefresh, setBudgetRefresh] = useState(0);

  // HF-4: From URL state
  const [urlInput, setUrlInput] = useState("");
  const [urlImporting, setUrlImporting] = useState(false);
  const [urlPreview, setUrlPreview] = useState(null);
  const [urlSaving, setUrlSaving] = useState(false);
  const [urlResult, setUrlResult] = useState(null);
  const [urlErr, setUrlErr] = useState(null);

  // ADMIN-10: KB stats state
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSort, setStatsSort] = useState("most");

  // KB-008: Groups state
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupPolicy, setNewGroupPolicy] = useState("");
  const [groupCreating, setGroupCreating] = useState(false);
  const [groupErr, setGroupErr] = useState(null);
  const [assignMode, setAssignMode] = useState(null); // groupId or "ungrouped"
  const [selectedEntryIds, setSelectedEntryIds] = useState(new Set());
  const [assignTarget, setAssignTarget] = useState(""); // target group ID for assignment
  const [assignBusy, setAssignBusy] = useState(false);
  const [budgetWarning, setBudgetWarning] = useState(null);
  const [regenBusy, setRegenBusy] = useState(null); // groupId being regenerated

  // KB-023: Batch Import state
  const [batchUrls, setBatchUrls] = useState("");
  const [batchImporting, setBatchImporting] = useState(false);
  const [batchPreview, setBatchPreview] = useState(null);
  const [batchUrlResults, setBatchUrlResults] = useState(null);
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [batchErr, setBatchErr] = useState(null);

  const pc = persona?.accentColor ?? "#3b82f6";
  const F = { width: "100%", padding: "11px 13px", background: "#0f1623", border: "1px solid #1e2d45", borderRadius: 9, color: "#e2e8f0", fontSize: 14, fontFamily: "inherit", outline: "none" };
  const L = { display: "block", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 6 };

  // Load entries whenever persona changes
  useEffect(() => {
    if (!persona?.id) return;
    setLoading(true);
    setLoadErr(null);
    getKnowledge(persona.id)
      .then(setEntries)
      .catch(() => setLoadErr("Failed to load entries"))
      .finally(() => setLoading(false));
  }, [persona?.id]);

  // Load groups when Groups tab selected or persona changes
  useEffect(() => {
    if (!persona?.id) return;
    // Eagerly load groups for the Entries tab grouped display too
    loadGroups();
  }, [persona?.id]);

  // ADMIN-10: Fetch KB stats when Stats tab is selected
  useEffect(() => {
    if (tab !== "Stats" || !persona?.id) return;
    setStatsLoading(true);
    apiFetch(`/api/admin/personas/${persona.id}/kb-stats`)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, [tab, persona?.id]);

  const loadGroups = () => {
    setGroupsLoading(true);
    getKnowledgeGroups(persona.id)
      .then(setGroups)
      .catch(() => setGroups([]))
      .finally(() => setGroupsLoading(false));
  };

  const reload = () => {
    setLoading(true);
    getKnowledge(persona.id)
      .then(setEntries)
      .catch(() => setLoadErr("Failed to reload entries"))
      .finally(() => setLoading(false));
    loadGroups();
    setBudgetRefresh(n => n + 1);
  };

  // Group entries by group_id for the Entries tab
  const { grouped, ungrouped } = useMemo(() => {
    const g = {};
    const u = [];
    for (const e of entries) {
      if (e.group_id) {
        if (!g[e.group_id]) g[e.group_id] = [];
        g[e.group_id].push(e);
      } else {
        u.push(e);
      }
    }
    return { grouped: g, ungrouped: u };
  }, [entries]);

  const openEdit = (entry) => {
    setEditTarget(entry);
    setForm({ topic: entry.topic, content: entry.content, source_title: entry.source_title ?? "", source_url: entry.source_url ?? "", group_id: entry.group_id ?? "" });
    setSaveOk(false);
    setSaveErr(null);
    setTab("Edit");
  };

  const handleAdd = async () => {
    if (!form.topic || !form.content || saving) return;
    setSaving(true); setSaveErr(null); setSaveOk(false); setBudgetWarning(null);
    try {
      const result = await addKnowledgeEntry(persona.id, {
        topic: form.topic, content: form.content,
        sourceTitle: form.source_title, sourceUrl: form.source_url,
        groupId: form.group_id || undefined,
      });
      setForm(EMPTY_ENTRY);
      setSaveOk(true);
      if (result.budgetWarning) setBudgetWarning(result.budgetWarning);
      setTimeout(() => { setSaveOk(false); setBudgetWarning(null); }, 4000);
      reload();
      setTab("Entries");
    } catch (e) {
      setSaveErr(e.message ?? "Save failed");
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!form.topic || !form.content || saving || !editTarget) return;
    setSaving(true); setSaveErr(null); setSaveOk(false);
    try {
      await updateKnowledgeEntry(editTarget.id, { ...form, groupId: form.group_id || null });
      setSaveOk(true);
      setTimeout(() => { setSaveOk(false); setTab("Entries"); }, 1200);
      reload();
    } catch (e) {
      setSaveErr(e.message ?? "Update failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.topic}"?`)) return;
    try {
      await deleteKnowledgeEntry(entry.id);
      reload();
    } catch { /* ignore */ }
  };

  // KB-008: Group actions
  const handleCreateGroup = async () => {
    if (!newGroupName || groupCreating) return;
    setGroupCreating(true); setGroupErr(null); setBudgetWarning(null);
    try {
      const result = await createKnowledgeGroup(persona.id, { name: newGroupName, policyArea: newGroupPolicy || undefined });
      setNewGroupName(""); setNewGroupPolicy("");
      if (result.budgetWarning) setBudgetWarning(result.budgetWarning);
      setTimeout(() => setBudgetWarning(null), 4000);
      loadGroups();
      reload();
    } catch (e) {
      setGroupErr(e.message ?? "Failed to create group");
    } finally { setGroupCreating(false); }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete group "${group.name}"? Entries will become ungrouped.`)) return;
    try {
      await deleteKnowledgeGroup(group.id);
      reload();
    } catch { /* ignore */ }
  };

  const handleRegenSummary = async (groupId) => {
    setRegenBusy(groupId);
    try {
      await regenerateGroupSummary(groupId);
      loadGroups();
    } catch { /* ignore */ }
    finally { setRegenBusy(null); }
  };

  const toggleExpanded = (groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  const toggleEntrySelection = (entryId) => {
    setSelectedEntryIds(prev => {
      const next = new Set(prev);
      next.has(entryId) ? next.delete(entryId) : next.add(entryId);
      return next;
    });
  };

  const startAssignMode = (sourceGroupId) => {
    setAssignMode(sourceGroupId);
    setSelectedEntryIds(new Set());
    setAssignTarget("");
  };

  const cancelAssignMode = () => {
    setAssignMode(null);
    setSelectedEntryIds(new Set());
    setAssignTarget("");
  };

  const handleAssign = async () => {
    if (selectedEntryIds.size === 0 || assignBusy) return;
    setAssignBusy(true);
    try {
      const ids = [...selectedEntryIds];
      if (assignTarget === "ungrouped") {
        // Unassign from current group
        await unassignEntriesFromGroup(assignMode, ids);
      } else if (assignTarget) {
        await assignEntriesToGroup(assignTarget, ids);
      }
      cancelAssignMode();
      reload();
    } catch { /* ignore */ }
    finally { setAssignBusy(false); }
  };

  const visibleTabs = tab === "Edit"
    ? ["Entries", "Groups", "Add Entry", "From URL", "Batch Import", "Edit", "Stats"]
    : ["Entries", "Groups", "Add Entry", "From URL", "Batch Import", "Stats"];

  const handleImportFromUrl = async () => {
    if (!urlInput || urlImporting) return;
    setUrlImporting(true); setUrlErr(null); setUrlResult(null); setUrlPreview(null); setBudgetWarning(null);
    try {
      const data = await importFromUrl(persona.id, urlInput, {
        onProgress: (job) => {
          if (job.progress?.message) setUrlErr(null); // clear any stale error during polling
        },
      });
      setUrlPreview(data.preview.map(e => ({ ...e, checked: true })));
      setUrlInput("");
      if (data.budget?.warning) setBudgetWarning(data.budget.warningMessage);
    } catch (e) {
      setUrlErr(e.message ?? "Import failed");
    } finally { setUrlImporting(false); }
  };

  const handleAddSelected = async () => {
    const selected = (urlPreview ?? []).filter(e => e.checked);
    if (!selected.length || urlSaving) return;
    setUrlSaving(true); setUrlErr(null); setBudgetWarning(null);
    let imported = 0;
    try {
      for (const e of selected) {
        const result = await addKnowledgeEntry(persona.id, {
          topic: e.topic, content: e.content,
          sourceTitle: e.source_title, sourceUrl: e.source_url,
          groupId: e.suggested_group_id ?? undefined,
        });
        imported++;
        if (result.budgetWarning) setBudgetWarning(result.budgetWarning);
      }
      setUrlResult({ imported });
      setUrlPreview(null);
      reload();
    } catch (e) {
      if (imported > 0) {
        setUrlResult({ imported });
        reload();
      }
      setUrlErr(e.message ?? "Save failed");
    } finally { setUrlSaving(false); }
  };

  // KB-023: Batch import handlers (background job with polling)
  const handleBatchImport = async () => {
    const urls = batchUrls.split("\n").map(u => u.trim()).filter(u => u.startsWith("http"));
    if (!urls.length || batchImporting) return;
    setBatchImporting(true); setBatchErr(null); setBatchResult(null); setBatchPreview(null); setBatchUrlResults(null);
    try {
      const data = await batchImportFromUrls(persona.id, urls, {
        onProgress: (job) => {
          if (job.progress?.message) setBatchErr(null); // clear stale errors during polling
        },
      });
      setBatchPreview(data.preview.map(e => ({ ...e, checked: !e.isDuplicate })));
      setBatchUrlResults(data.urlResults);
      setBatchUrls("");
    } catch (e) {
      setBatchErr(e.message ?? "Batch import failed");
    } finally { setBatchImporting(false); }
  };

  const handleBatchSave = async () => {
    const selected = (batchPreview ?? []).filter(e => e.checked);
    if (!selected.length || batchSaving) return;
    setBatchSaving(true); setBatchErr(null);
    try {
      const result = await bulkSaveEntries(persona.id, selected.map(e => ({
        topic: e.topic, content: e.content,
        sourceUrl: e.sourceUrl, sourceTitle: e.sourceTitle,
        groupId: e.suggestedGroupId ?? undefined,
      })));
      setBatchResult({ saved: result.saved, errors: result.errors?.length ?? 0 });
      setBatchPreview(null);
      reload();
    } catch (e) {
      setBatchErr(e.message ?? "Bulk save failed");
    } finally { setBatchSaving(false); }
  };

  /** Render a single entry card */
  const EntryCard = ({ e, showAssignCheckbox = false }) => (
    <div key={e.id} style={{ background: "#0d1724", border: `1px solid ${showAssignCheckbox && selectedEntryIds.has(e.id) ? pc + "66" : "#1e2d45"}`, borderRadius: 12, padding: "12px 16px", display: "flex", gap: 10, alignItems: showAssignCheckbox ? "flex-start" : undefined }}>
      {showAssignCheckbox && (
        <div onClick={() => toggleEntrySelection(e.id)} style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selectedEntryIds.has(e.id) ? pc : "#334155"}`, background: selectedEntryIds.has(e.id) ? pc : "transparent", flexShrink: 0, marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          {selectedEntryIds.has(e.id) && <span style={{ color: "#fff", fontSize: 11, fontWeight: 900, lineHeight: 1 }}>✓</span>}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
          <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 13 }}>{e.topic}</div>
          {!showAssignCheckbox && (
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button onClick={() => openEdit(e)} style={{ padding: "3px 8px", background: "#1e2d45", border: "1px solid #2d3f5a", borderRadius: 6, color: "#94a3b8", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
              <button onClick={() => handleDelete(e)} style={{ padding: "3px 8px", background: "#ef444411", border: "1px solid #ef444430", borderRadius: 6, color: "#ef4444", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
            </div>
          )}
        </div>
        <p style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5, margin: "0 0 4px" }}>{e.content}</p>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {e.hit_count > 0 && (
            <span style={{ display: "inline-block", padding: "2px 7px", background: "#3b82f614", border: "1px solid #3b82f630", borderRadius: 5, fontSize: 9, color: "#60a5fa", fontWeight: 600 }}>
              Referenced {e.hit_count}x
            </span>
          )}
          {e.source_url && (
            <a href={e.source_url} target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", fontSize: 10, textDecoration: "none" }}>
              ↗ {e.source_title ?? e.source_url}
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: hideBackButton ? "0" : "32px 18px 60px" }}>
      {/* Header */}
      {!hideBackButton && (
        <>
          <button onClick={onBack} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 13, marginBottom: 24, padding: 0 }}>← Back</button>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
            {persona && <Ava ini={persona.avatar} color={pc} size={40} photoUrl={persona.photo_url ?? persona.photoUrl} />}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", color: pc, textTransform: "uppercase", marginBottom: 3 }}>Knowledge Base</div>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: "#f1f5f9", margin: 0, fontFamily: "Georgia,serif" }}>{persona?.name ?? "Persona"}</h1>
            </div>
          </div>
        </>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 11, padding: 4, gap: 3, marginBottom: 24 }}>
        {visibleTabs.map(t => (
          <button key={t} onClick={() => { setTab(t); setSaveErr(null); setSaveOk(false); if (t !== "Edit") { setEditTarget(null); setForm(EMPTY_ENTRY); } cancelAssignMode(); }}
            style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: tab === t ? "#1e3a5f" : "transparent", color: tab === t ? "#60a5fa" : "#64748b", fontSize: 11, fontWeight: tab === t ? 700 : 400, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>
            {t}{t === "Entries" && entries.length > 0 ? ` (${entries.length})` : ""}{t === "Groups" && groups.length > 0 ? ` (${groups.length})` : ""}
          </button>
        ))}
      </div>

      {/* KB-018: Context budget bar */}
      <ContextBudgetBar personaId={persona?.id} refreshKey={budgetRefresh} pc={pc} isAdmin={true} onLimitUpdated={() => setBudgetRefresh(r => r + 1)} />

      {/* KB-019: Budget warning toast */}
      {budgetWarning && (
        <div style={{ background: "#eab30811", border: "1px solid #eab30833", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "#eab308", lineHeight: 1.4 }}>
          {budgetWarning}
        </div>
      )}

      {/* ── Tab: Entries (grouped view) ──────────────────────────────────────────── */}
      {tab === "Entries" && (
        <div>
          {loading && <div style={{ color: "#475569", textAlign: "center", padding: 32 }}>Loading...</div>}
          {loadErr && <div style={{ color: "#ef4444", padding: 12, background: "#ef444411", borderRadius: 8 }}>{loadErr}</div>}
          {!loading && !loadErr && entries.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📚</div>
              <p style={{ color: "#475569", fontSize: 13 }}>No knowledge entries yet.</p>
              <button onClick={() => setTab("Add Entry")} style={{ padding: "9px 20px", background: `${pc}22`, border: `1px solid ${pc}44`, borderRadius: 9, color: pc, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                + Add First Entry
              </button>
            </div>
          )}
          {!loading && entries.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Grouped entries */}
              {groups.filter(g => grouped[g.id]?.length > 0).map(group => (
                <div key={group.id} style={{ border: "1px solid #1e2d45", borderRadius: 14, overflow: "hidden" }}>
                  <div onClick={() => toggleExpanded(group.id)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#0a1120", cursor: "pointer", userSelect: "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: "#475569", fontSize: 12, transition: "transform .15s", transform: expandedGroups.has(group.id) ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                      <span style={{ fontWeight: 700, color: pc, fontSize: 13 }}>{group.name}</span>
                      <span style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>({grouped[group.id]?.length ?? 0})</span>
                    </div>
                    {group.policy_area && (
                      <span style={{ padding: "2px 8px", background: `${pc}14`, borderRadius: 5, fontSize: 10, color: pc, fontWeight: 600 }}>{group.policy_area}</span>
                    )}
                  </div>
                  {expandedGroups.has(group.id) && (
                    <div style={{ padding: "8px 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {group.summary && (
                        <div style={{ padding: "8px 12px", background: "#0f162388", borderRadius: 8, fontSize: 11, color: "#94a3b8", lineHeight: 1.5, fontStyle: "italic" }}>
                          {group.summary}
                        </div>
                      )}
                      {(grouped[group.id] ?? []).map(e => <EntryCard key={e.id} e={e} />)}
                    </div>
                  )}
                </div>
              ))}

              {/* Ungrouped entries */}
              {ungrouped.length > 0 && (
                <div style={{ border: "1px solid #1e2d45", borderRadius: 14, overflow: "hidden" }}>
                  <div onClick={() => toggleExpanded("ungrouped")}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#0a1120", cursor: "pointer", userSelect: "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: "#475569", fontSize: 12, transition: "transform .15s", transform: expandedGroups.has("ungrouped") ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                      <span style={{ fontWeight: 700, color: "#94a3b8", fontSize: 13 }}>Ungrouped</span>
                      <span style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>({ungrouped.length})</span>
                    </div>
                  </div>
                  {expandedGroups.has("ungrouped") && (
                    <div style={{ padding: "8px 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {ungrouped.map(e => <EntryCard key={e.id} e={e} />)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Groups (KB-008) ──────────────────────────────────────────────────── */}
      {tab === "Groups" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Create group form */}
          <div style={{ padding: "16px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>Create Group</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input style={{ ...F, flex: 2 }} placeholder="Group name (e.g. Fuel Subsidy)" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} />
              <input style={{ ...F, flex: 1 }} placeholder="Policy area (optional)" value={newGroupPolicy} onChange={e => setNewGroupPolicy(e.target.value)} />
            </div>
            {groupErr && <div style={{ color: "#ef4444", fontSize: 11, marginBottom: 8 }}>{groupErr}</div>}
            <button onClick={handleCreateGroup} disabled={groupCreating || !newGroupName}
              style={{ padding: "9px 20px", background: groupCreating || !newGroupName ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}bb)`, color: groupCreating || !newGroupName ? "#475569" : "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: groupCreating || !newGroupName ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {groupCreating ? "Creating..." : "+ Create Group"}
            </button>
          </div>

          {/* Assign mode bar */}
          {assignMode && (
            <div style={{ padding: "12px 16px", background: `${pc}14`, border: `1px solid ${pc}44`, borderRadius: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: pc, fontWeight: 600 }}>Select entries to move:</span>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>{selectedEntryIds.size} selected</span>
              <span style={{ flex: 1 }} />
              <select value={assignTarget} onChange={e => setAssignTarget(e.target.value)}
                style={{ padding: "5px 10px", background: "#0f1623", border: "1px solid #1e2d45", borderRadius: 6, color: "#e2e8f0", fontSize: 11, fontFamily: "inherit" }}>
                <option value="">Move to...</option>
                {assignMode !== "ungrouped" && <option value="ungrouped">Ungrouped</option>}
                {groups.filter(g => g.id !== assignMode).map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              <button onClick={handleAssign} disabled={assignBusy || selectedEntryIds.size === 0 || !assignTarget}
                style={{ padding: "5px 14px", background: assignBusy || selectedEntryIds.size === 0 || !assignTarget ? "#1e2d45" : pc, color: assignBusy || selectedEntryIds.size === 0 || !assignTarget ? "#475569" : "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: assignBusy || selectedEntryIds.size === 0 || !assignTarget ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {assignBusy ? "Moving..." : "Move"}
              </button>
              <button onClick={cancelAssignMode}
                style={{ padding: "5px 10px", background: "transparent", border: "1px solid #334155", borderRadius: 6, color: "#64748b", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          )}

          {groupsLoading && <div style={{ color: "#475569", textAlign: "center", padding: 32 }}>Loading groups...</div>}

          {!groupsLoading && groups.length === 0 && (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#475569", fontSize: 13 }}>
              No groups yet. Create one above to organize entries by topic.
            </div>
          )}

          {/* Group cards */}
          {!groupsLoading && groups.map(group => {
            const groupEntries = grouped[group.id] ?? [];
            const isExpanded = expandedGroups.has(group.id);
            const isAssigning = assignMode === group.id;

            return (
              <div key={group.id} style={{ border: "1px solid #1e2d45", borderRadius: 14, overflow: "hidden" }}>
                {/* Group header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#0a1120" }}>
                  <div onClick={() => toggleExpanded(group.id)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}>
                    <span style={{ color: "#475569", fontSize: 12, transition: "transform .15s", transform: isExpanded ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                    <span style={{ fontWeight: 700, color: pc, fontSize: 13 }}>{group.name}</span>
                    <span style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>({group.entry_count})</span>
                    {group.policy_area && (
                      <span style={{ padding: "2px 7px", background: `${pc}14`, borderRadius: 5, fontSize: 9, color: pc, fontWeight: 600 }}>{group.policy_area}</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => startAssignMode(group.id)}
                      style={{ padding: "3px 8px", background: "#1e2d45", border: "1px solid #2d3f5a", borderRadius: 5, color: "#94a3b8", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>
                      Move entries
                    </button>
                    <button onClick={() => handleRegenSummary(group.id)} disabled={regenBusy === group.id}
                      style={{ padding: "3px 8px", background: "#1e2d45", border: "1px solid #2d3f5a", borderRadius: 5, color: "#94a3b8", fontSize: 9, cursor: regenBusy === group.id ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                      {regenBusy === group.id ? "..." : "Regen summary"}
                    </button>
                    <button onClick={() => handleDeleteGroup(group)}
                      style={{ padding: "3px 8px", background: "#ef444411", border: "1px solid #ef444430", borderRadius: 5, color: "#ef4444", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>
                      Delete
                    </button>
                  </div>
                </div>

                {/* Group body */}
                {isExpanded && (
                  <div style={{ padding: "8px 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {group.summary && (
                      <div style={{ padding: "8px 12px", background: "#0f162388", borderRadius: 8, fontSize: 11, color: "#94a3b8", lineHeight: 1.5, fontStyle: "italic" }}>
                        {group.summary}
                      </div>
                    )}
                    {groupEntries.length === 0 && (
                      <div style={{ color: "#475569", fontSize: 12, padding: "12px 0", textAlign: "center" }}>No entries in this group yet.</div>
                    )}
                    {groupEntries.map(e => (
                      <EntryCard key={e.id} e={e} showAssignCheckbox={isAssigning} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Ungrouped section in Groups tab */}
          {!groupsLoading && ungrouped.length > 0 && (
            <div style={{ border: "1px solid #1e2d45", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#0a1120" }}>
                <div onClick={() => toggleExpanded("ungrouped-manage")} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}>
                  <span style={{ color: "#475569", fontSize: 12, transition: "transform .15s", transform: expandedGroups.has("ungrouped-manage") ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                  <span style={{ fontWeight: 700, color: "#94a3b8", fontSize: 13 }}>Ungrouped</span>
                  <span style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>({ungrouped.length})</span>
                </div>
                <button onClick={() => startAssignMode("ungrouped")}
                  style={{ padding: "3px 8px", background: "#1e2d45", border: "1px solid #2d3f5a", borderRadius: 5, color: "#94a3b8", fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>
                  Move entries
                </button>
              </div>
              {expandedGroups.has("ungrouped-manage") && (
                <div style={{ padding: "8px 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {ungrouped.map(e => (
                    <EntryCard key={e.id} e={e} showAssignCheckbox={assignMode === "ungrouped"} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Add Entry ─────────────────────────────────────────────────────── */}
      {tab === "Add Entry" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={L}>Topic *</label>
            <input style={F} placeholder="e.g. Economic Policy 2024" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} />
          </div>
          <div>
            <label style={L}>Content *</label>
            <textarea style={{ ...F, minHeight: 120, resize: "vertical", lineHeight: 1.6 }}
              placeholder="Detailed factual content that will be used to answer deep-dive questions..."
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={L}>Source Title</label>
              <input style={F} placeholder="e.g. Official Website" value={form.source_title} onChange={e => setForm(f => ({ ...f, source_title: e.target.value }))} />
            </div>
            <div>
              <label style={L}>Source URL</label>
              <input style={F} placeholder="https://..." value={form.source_url} onChange={e => setForm(f => ({ ...f, source_url: e.target.value }))} />
            </div>
          </div>
          {saveErr && <div style={{ color: "#ef4444", fontSize: 12, padding: "8px 12px", background: "#ef444411", borderRadius: 7 }}>{saveErr}</div>}
          {saveOk && <div style={{ color: "#22c55e", fontSize: 12, padding: "8px 12px", background: "#22c55e11", borderRadius: 7 }}>✓ Entry added to knowledge base</div>}
          <button onClick={handleAdd} disabled={saving || !form.topic || !form.content}
            style={{ padding: "13px", background: saving || !form.topic || !form.content ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}bb)`, color: saving || !form.topic || !form.content ? "#475569" : "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: saving || !form.topic || !form.content ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {saving ? "Saving..." : "Add Entry"}
          </button>
        </div>
      )}

      {/* ── Tab: From URL ──────────────────────────────────────────────────────── */}
      {tab === "From URL" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ padding: "10px 14px", background: "#0f1623", border: "1px solid #1e2d45", borderRadius: 9, fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
            Paste any public web URL (news article, official website, campaign page) and Claude will extract knowledge base entries for you to review before saving.
          </div>
          {!urlPreview && (
            <>
              <div>
                <label style={L}>Page URL *</label>
                <input style={F} placeholder="https://..." value={urlInput} onChange={e => setUrlInput(e.target.value)} />
              </div>
              {urlErr && <div style={{ color: "#ef4444", fontSize: 12, padding: "8px 12px", background: "#ef444411", borderRadius: 7 }}>{urlErr}</div>}
              {urlResult && (
                <div style={{ padding: "10px 14px", background: "#22c55e11", border: "1px solid #22c55e30", borderRadius: 8, fontSize: 12, color: "#22c55e" }}>
                  Added {urlResult.imported} entries to the knowledge base
                </div>
              )}
              <button onClick={handleImportFromUrl} disabled={urlImporting || !urlInput}
                style={{ padding: "13px", background: urlImporting || !urlInput ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}bb)`, color: urlImporting || !urlInput ? "#475569" : "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: urlImporting || !urlInput ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {urlImporting ? "Fetching & extracting..." : "Fetch from URL"}
              </button>
            </>
          )}
          {urlPreview && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                Review extracted entries — uncheck any you don't want to add:
              </div>
              {urlPreview.map((e, i) => (
                <div key={i} style={{ background: "#0d1724", border: `1px solid ${e.checked ? pc + "44" : "#1e2d45"}`, borderRadius: 10, padding: "12px 14px", display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}
                  onClick={() => setUrlPreview(prev => prev.map((x, j) => j === i ? { ...x, checked: !x.checked } : x))}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${e.checked ? pc : "#334155"}`, background: e.checked ? pc : "transparent", flexShrink: 0, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {e.checked && <span style={{ color: "#fff", fontSize: 11, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 13, marginBottom: 4 }}>{e.topic}</div>
                    <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>{e.content}</div>
                    {e.suggested_group_name && (
                      <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Group:</span>
                        <span style={{ fontSize: 11, color: pc, fontWeight: 600, background: pc + "18", padding: "2px 8px", borderRadius: 4 }}>
                          {e.suggested_group_name}{e.group_created ? " (new)" : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {urlErr && <div style={{ color: "#ef4444", fontSize: 12, padding: "8px 12px", background: "#ef444411", borderRadius: 7 }}>{urlErr}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setUrlPreview(null); setUrlErr(null); }}
                  style={{ flex: 1, padding: "11px", background: "transparent", border: "1px solid #1e2d45", borderRadius: 9, color: "#64748b", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Fetch different URL
                </button>
                <button onClick={handleAddSelected} disabled={urlSaving || !urlPreview.some(e => e.checked)}
                  style={{ flex: 2, padding: "11px", background: urlSaving || !urlPreview.some(e => e.checked) ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}bb)`, color: urlSaving || !urlPreview.some(e => e.checked) ? "#475569" : "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: urlSaving || !urlPreview.some(e => e.checked) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  {urlSaving ? "Saving..." : `Add ${urlPreview.filter(e => e.checked).length} Selected`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Batch Import (KB-023) ──────────────────────────────────────────── */}
      {tab === "Batch Import" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ padding: "10px 14px", background: "#0f1623", border: "1px solid #1e2d45", borderRadius: 9, fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
            Paste up to 10 URLs (one per line). Claude will extract entries from each, flag duplicates, and suggest groups. Review before saving.
          </div>

          {!batchPreview && (
            <>
              <div>
                <label style={L}>URLs (one per line, max 10) *</label>
                <textarea style={{ ...F, minHeight: 100, resize: "vertical", lineHeight: 1.6, fontFamily: "monospace", fontSize: 12 }}
                  placeholder={"https://example.com/page-1\nhttps://example.com/page-2"}
                  value={batchUrls}
                  onChange={e => setBatchUrls(e.target.value)}
                />
              </div>
              {batchErr && <div style={{ color: "#ef4444", fontSize: 12, padding: "8px 12px", background: "#ef444411", borderRadius: 7 }}>{batchErr}</div>}
              {batchResult && (
                <div style={{ padding: "10px 14px", background: "#22c55e11", border: "1px solid #22c55e30", borderRadius: 8, fontSize: 12, color: "#22c55e" }}>
                  Saved {batchResult.saved} entries{batchResult.errors > 0 ? ` (${batchResult.errors} failed)` : ""}
                </div>
              )}
              <button onClick={handleBatchImport}
                disabled={batchImporting || !batchUrls.split("\n").some(u => u.trim().startsWith("http"))}
                style={{
                  padding: "13px",
                  background: batchImporting || !batchUrls.split("\n").some(u => u.trim().startsWith("http")) ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}bb)`,
                  color: batchImporting || !batchUrls.split("\n").some(u => u.trim().startsWith("http")) ? "#475569" : "#fff",
                  border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: batchImporting ? "not-allowed" : "pointer", fontFamily: "inherit",
                }}>
                {batchImporting ? "Fetching & extracting..." : "Fetch All URLs"}
              </button>
            </>
          )}

          {batchUrlResults && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {batchUrlResults.map((r, i) => (
                <div key={i} style={{
                  padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                  background: r.status === "ok" ? "#22c55e11" : "#ef444411",
                  color: r.status === "ok" ? "#22c55e" : "#ef4444",
                  border: `1px solid ${r.status === "ok" ? "#22c55e30" : "#ef444430"}`,
                  maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {r.status === "ok" ? "OK" : "ERR"}: {(() => { try { return new URL(r.url).hostname; } catch { return r.url; } })()}
                </div>
              ))}
            </div>
          )}

          {batchPreview && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                  {batchPreview.length} entries extracted:
                </div>
                <div style={{ fontSize: 11, color: "#64748b" }}>
                  {batchPreview.filter(e => e.isDuplicate).length} duplicate{batchPreview.filter(e => e.isDuplicate).length !== 1 ? "s" : ""}
                </div>
              </div>

              {batchPreview.map((e, i) => (
                <div key={e.key} style={{
                  background: "#0d1724",
                  border: `1px solid ${e.isDuplicate ? "#ef444430" : e.checked ? pc + "44" : "#1e2d45"}`,
                  borderRadius: 10, padding: "12px 14px",
                  display: "flex", gap: 12, alignItems: "flex-start",
                  cursor: "pointer", opacity: e.isDuplicate && !e.checked ? 0.5 : 1,
                }}
                  onClick={() => setBatchPreview(prev => prev.map((x, j) => j === i ? { ...x, checked: !x.checked } : x))}>
                  <div style={{
                    width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                    border: `2px solid ${e.checked ? pc : "#334155"}`,
                    background: e.checked ? pc : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {e.checked && <span style={{ color: "#fff", fontSize: 11, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 13 }}>{e.topic}</span>
                      {e.isDuplicate && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: "#ef4444", background: "#ef444418", padding: "2px 6px", borderRadius: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Duplicate ({Math.round((e.duplicateSimilarity ?? 0) * 100)}%)
                        </span>
                      )}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5, marginBottom: 4 }}>{e.content}</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, color: "#475569" }}>{e.sourceTitle}</span>
                      {e.suggestedGroupName && (
                        <span style={{ fontSize: 10, color: pc, fontWeight: 600, background: pc + "18", padding: "2px 8px", borderRadius: 4 }}>
                          {e.suggestedGroupName}{e.groupCreated ? " (new)" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {batchErr && <div style={{ color: "#ef4444", fontSize: 12, padding: "8px 12px", background: "#ef444411", borderRadius: 7 }}>{batchErr}</div>}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setBatchPreview(null); setBatchUrlResults(null); setBatchErr(null); }}
                  style={{ flex: 1, padding: "11px", background: "transparent", border: "1px solid #1e2d45", borderRadius: 9, color: "#64748b", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Start Over
                </button>
                <button onClick={handleBatchSave}
                  disabled={batchSaving || !batchPreview.some(e => e.checked)}
                  style={{
                    flex: 2, padding: "11px", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                    background: batchSaving || !batchPreview.some(e => e.checked) ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}bb)`,
                    color: batchSaving || !batchPreview.some(e => e.checked) ? "#475569" : "#fff",
                    cursor: batchSaving || !batchPreview.some(e => e.checked) ? "not-allowed" : "pointer",
                  }}>
                  {batchSaving ? "Saving..." : `Save ${batchPreview.filter(e => e.checked).length} Selected`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Stats (ADMIN-10) ──────────────────────────────────────────────── */}
      {tab === "Stats" && (
        <div>
          {statsLoading && <div style={{ color: "#475569", textAlign: "center", padding: 32 }}>Loading stats...</div>}
          {!statsLoading && (!stats || !stats.entries?.length) && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📊</div>
              <p style={{ color: "#475569", fontSize: 13 }}>No knowledge base references yet.</p>
              <p style={{ color: "#334155", fontSize: 12 }}>KB entries are counted each time the AI uses them to answer a question.</p>
            </div>
          )}
          {!statsLoading && stats?.entries?.length > 0 && (() => {
            const unreferenced = stats.entries.filter(e => e.hitCount === 0).length;
            const sorted = [...stats.entries].sort((a, b) =>
              statsSort === "fewest" ? a.hitCount - b.hitCount
              : statsSort === "recent" ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              : b.hitCount - a.hitCount
            );
            return (
              <>
                <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
                  <div style={{ padding: "10px 16px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 10, flex: 1, minWidth: 140 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Total References</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "#60a5fa" }}>{stats.totalHits}</div>
                  </div>
                  <div style={{ padding: "10px 16px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 10, flex: 1, minWidth: 140 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Entries</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "#e2e8f0" }}>{stats.entries.length}</div>
                  </div>
                  <div style={{ padding: "10px 16px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 10, flex: 1, minWidth: 140 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Unreferenced</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: unreferenced > 0 ? "#f59e0b" : "#22c55e" }}>{unreferenced}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                  {[["most", "Most hits"], ["fewest", "Fewest hits"], ["recent", "Recently added"]].map(([key, label]) => (
                    <button key={key} onClick={() => setStatsSort(key)}
                      style={{ padding: "6px 14px", borderRadius: 7, border: statsSort === key ? `1px solid ${pc}66` : "1px solid #1e2d45", background: statsSort === key ? `${pc}22` : "transparent", color: statsSort === key ? pc : "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      {label}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {sorted.map((e, i) => (
                    <div key={e.id} style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 10, padding: "12px 16px", opacity: e.hitCount === 0 ? 0.5 : 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", minWidth: 20 }}>#{i + 1}</span>
                          <span style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 13 }}>{e.topic}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: e.hitCount > 0 ? "#60a5fa" : "#475569" }}>{e.hitCount}</span>
                          <span style={{ fontSize: 11, color: "#64748b" }}>{e.hitPercent}%</span>
                        </div>
                      </div>
                      <div style={{ height: 6, background: "#1e2d45", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${Math.max(e.hitPercent, e.hitCount > 0 ? 2 : 0)}%`, height: "100%", background: e.hitCount > 0 ? `linear-gradient(90deg, ${pc}, ${pc}bb)` : "transparent", borderRadius: 3, transition: "width 0.3s ease" }} />
                      </div>
                      {e.sourceTitle && (
                        <div style={{ fontSize: 10, color: "#475569", marginTop: 6 }}>{e.sourceTitle}</div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ── Tab: Edit ──────────────────────────────────────────────────────────── */}
      {tab === "Edit" && editTarget && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={L}>Topic *</label>
            <input style={F} value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} />
          </div>
          <div>
            <label style={L}>Content *</label>
            <textarea style={{ ...F, minHeight: 120, resize: "vertical", lineHeight: 1.6 }}
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={L}>Source Title</label>
              <input style={F} value={form.source_title} onChange={e => setForm(f => ({ ...f, source_title: e.target.value }))} />
            </div>
            <div>
              <label style={L}>Source URL</label>
              <input style={F} value={form.source_url} onChange={e => setForm(f => ({ ...f, source_url: e.target.value }))} />
            </div>
          </div>
          <div>
            <label style={L}>Group</label>
            <select style={{ ...F, cursor: "pointer" }} value={form.group_id} onChange={e => setForm(f => ({ ...f, group_id: e.target.value }))}>
              <option value="">Ungrouped</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name}{g.entry_count != null ? ` (${g.entry_count})` : ""}</option>
              ))}
            </select>
          </div>
          {saveErr && <div style={{ color: "#ef4444", fontSize: 12, padding: "8px 12px", background: "#ef444411", borderRadius: 7 }}>{saveErr}</div>}
          {saveOk && <div style={{ color: "#22c55e", fontSize: 12, padding: "8px 12px", background: "#22c55e11", borderRadius: 7 }}>Entry updated</div>}
          <button onClick={handleUpdate} disabled={saving || !form.topic || !form.content}
            style={{ padding: "13px", background: saving || !form.topic || !form.content ? "#1e2d45" : `linear-gradient(135deg,${pc},${pc}bb)`, color: saving || !form.topic || !form.content ? "#475569" : "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: saving || !form.topic || !form.content ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}

export default KnowledgeAdmin;
